import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, Eye, User, Users, Mail, Phone, 
  MapPin, Building, Building2, CreditCard, ChevronLeft, ChevronRight, 
  ChevronUp, ChevronDown,
  X, AlertCircle, CheckCircle, XCircle, Filter, Download, Upload, 
  Calendar, FileText, DollarSign, TrendingUp, TrendingDown, Clock, 
  Settings, Package, ShoppingBag, Smartphone, Copy, Star, Sparkles,
  Percent, Tag, Boxes, AlertTriangle, Coffee, BarChart3,
  ShoppingCart, LogOut, Menu, FileSpreadsheet, Image as ImageIcon
} from 'lucide-react';
import { articulosService,proveedoresService, categoriasService, articulosPreciosProveedorService, ubicacionesAlmacenService, UPLOADS_BASE_URL  } from '../../../services/api';
import { useApp } from '../../../context/AppContext';

    // onFeedback: la funcion showFeedback del componente que llama (D2 --
    // esta funcion es de modulo, sin estado propio, no puede mostrar su
    // propio modal). Sin onFeedback, no muestra nada (solo copia).
    const copiarAlPortapapeles = (texto, mensaje = 'Copiado al portapapeles', onFeedback) => {
    navigator.clipboard.writeText(texto).then(() => {
        if (onFeedback) onFeedback('success', 'Copiado', mensaje);
    }).catch(err => {
        console.error('Error al copiar:', err);
        if (onFeedback) onFeedback('error', 'Error', 'Error al copiar al portapapeles');
    });
    };
    const FeedbackModal = ({ isOpen, type, title, message, onConfirm, onClose, showCancel }) => {
      if (!isOpen) return null;

      const icons = {
        success: <CheckCircle className="text-green-500 w-12 h-12" />,
        error: <XCircle className="text-red-500 w-12 h-12" />,
        warning: <AlertTriangle className="text-yellow-500 w-12 h-12" />,
        info: <AlertCircle className="text-blue-500 w-12 h-12" />
      };

      const colors = {
        success: 'bg-green-600 hover:bg-green-700',
        error: 'bg-red-600 hover:bg-red-700',
        warning: 'bg-yellow-600 hover:bg-yellow-700',
        info: 'bg-blue-600 hover:bg-blue-700'
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all">
            <div className="flex justify-center mb-4">
              {icons[type] || icons.info}
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
            <p className="text-sm text-gray-600 mb-6">{message}</p>
            <div className="flex justify-center gap-3">
              {showCancel && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
              )}
              <button
                onClick={() => {
                  if (onConfirm) onConfirm();
                  onClose();
                }}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${colors[type] || colors.info}`}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      );
    };
    
    // Precios del mismo articulo cotizados por varios proveedores, para
    // comparar antes de decidir a quien comprarle. No sustituye el
    // proveedor/precio_costo "principal" del articulo (Sección 2), que
    // sigue siendo el que se usa para costear.
    const PreciosProveedoresSection = ({ articuloId, proveedores, isViewMode, showFeedback }) => {
      const [precios, setPrecios] = useState([]);
      const [cargando, setCargando] = useState(true);
      const [error, setError] = useState('');
      const [proveedorId, setProveedorId] = useState('');
      const [precioCosto, setPrecioCosto] = useState('');
      const [moneda, setMoneda] = useState('USD');
      const [guardando, setGuardando] = useState(false);

      const cargar = async () => {
        if (!articuloId) { setCargando(false); return; }
        setCargando(true);
        try {
          const res = await articulosPreciosProveedorService.getAll(articuloId);
          setPrecios(res.data || []);
        } catch (err) {
          setError(err.message || 'Error al cargar precios por proveedor');
        } finally {
          setCargando(false);
        }
      };

      useEffect(() => { cargar(); }, [articuloId]); // eslint-disable-line react-hooks/exhaustive-deps

      const handleAgregar = async () => {
        if (!proveedorId || !precioCosto) return;
        setError('');
        setGuardando(true);
        try {
          await articulosPreciosProveedorService.guardar(articuloId, {
            proveedor_id: parseInt(proveedorId, 10),
            precio_costo: parseFloat(precioCosto),
            moneda
          });
          setProveedorId('');
          setPrecioCosto('');
          await cargar();
        } catch (err) {
          setError(err.message || 'Error al guardar el precio');
        } finally {
          setGuardando(false);
        }
      };

      const handleEliminar = async (id) => {
        const confirmarEliminar = async () => {
          try {
            await articulosPreciosProveedorService.eliminar(id);
            await cargar();
          } catch (err) {
            showFeedback('error', 'Error', err.message || 'Error al eliminar el precio');
          }
        };
        showFeedback('warning', 'Eliminar precio', '¿Eliminar este precio?', confirmarEliminar, true);
      };

      return (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users size={20} />
            Precios por Proveedor
          </h4>
          <p className="text-xs text-gray-500 -mt-3 mb-4">
            Comparación entre proveedores. El proveedor y precio de costo de la sección "Categoría y Proveedor" es el que se usa para costear.
          </p>

          {!articuloId ? (
            <p className="text-sm text-gray-400 italic">Guarda el artículo primero para poder agregar precios de otros proveedores.</p>
          ) : (
            <>
              {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-3 text-sm">{error}</div>}

              {cargando ? (
                <p className="text-sm text-gray-400">Cargando...</p>
              ) : precios.length === 0 ? (
                <p className="text-sm text-gray-400 italic mb-3">Sin precios de otros proveedores registrados todavía.</p>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                        <th className="px-3 py-2">Proveedor</th>
                        <th className="px-3 py-2 text-right">Precio de costo</th>
                        {!isViewMode && <th className="px-3 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {precios.map((p) => (
                        <tr key={p.id} className="border-b border-gray-100">
                          <td className="px-3 py-2">{p.proveedor_nombre}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {p.moneda === 'VES' ? `Bs. ${Number(p.precio_costo).toFixed(2)}` : `$${Number(p.precio_costo).toFixed(2)}`}
                          </td>
                          {!isViewMode && (
                            <td className="px-3 py-2 text-right">
                              <button type="button" onClick={() => handleEliminar(p.id)} className="text-red-500 hover:text-red-700">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isViewMode && (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs text-gray-600 mb-1">Proveedor</label>
                    <select
                      value={proveedorId}
                      onChange={(e) => setProveedorId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Selecciona...</option>
                      {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-gray-600 mb-1">Moneda</label>
                    <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="USD">USD</option>
                      <option value="VES">VES</option>
                    </select>
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 mb-1">Precio de costo</label>
                    <input
                      type="number" step="0.01" value={precioCosto}
                      onChange={(e) => setPrecioCosto(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAgregar}
                    disabled={guardando || !proveedorId || !precioCosto}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm font-medium flex items-center gap-1"
                  >
                    <Plus size={14} /> Agregar
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      );
    };

    const NIVEL_LABEL = { almacen: 'Almacén', zona: 'Zona', area: 'Área', rack: 'Rack' };
    const NIVEL_ANTERIOR = { zona: 'almacen', area: 'zona', rack: 'area' };

    // Selector en cascada Almacén > Zona > Área > Rack. `ubicacionId` puede
    // apuntar a cualquier nivel del árbol (no se obliga a llegar a rack).
    // Cada nivel permite crear una opción nueva sin salir del formulario.
    const UbicacionSelector = ({ arbol, ubicacionId, onChange, onArbolChange, disabled, showFeedback }) => {
      const nodo = (id) => arbol.find(n => n.id === Number(id));

      // A partir del nodo seleccionado, reconstruye el id vigente en cada
      // nivel subiendo por padre_id (para preseleccionar los 4 combos).
      const seleccionPorNivel = () => {
        const sel = { almacen: '', zona: '', area: '', rack: '' };
        let actual = ubicacionId ? nodo(ubicacionId) : null;
        while (actual) {
          sel[actual.nivel] = actual.id;
          actual = actual.padre_id ? nodo(actual.padre_id) : null;
        }
        return sel;
      };

      const sel = seleccionPorNivel();
      const [nuevoNivel, setNuevoNivel] = useState(null); // nivel con el mini-formulario "+" abierto
      const [nuevoNombre, setNuevoNombre] = useState('');
      const [creando, setCreando] = useState(false);

      const hijosDe = (nivel, padreId) =>
        arbol.filter(n => n.nivel === nivel && n.activo && (nivel === 'almacen' ? true : n.padre_id === Number(padreId)));

      const seleccionar = (nivel, id) => {
        if (!id) {
          // limpiar este nivel: retrocede al padre (o a nada si era almacén)
          const nivelPadre = NIVEL_ANTERIOR[nivel];
          onChange(nivelPadre ? (sel[nivelPadre] || null) : null);
          return;
        }
        onChange(Number(id));
      };

      const crearAqui = async (nivel, padreId) => {
        if (!nuevoNombre.trim()) return;
        setCreando(true);
        try {
          const resp = await ubicacionesAlmacenService.create({ nivel, nombre: nuevoNombre.trim(), padre_id: padreId || null });
          onArbolChange(prev => [...prev, resp.data]);
          onChange(resp.data.id);
          setNuevoNivel(null);
          setNuevoNombre('');
        } catch (error) {
          showFeedback('error', 'Error', error.message || `No se pudo crear ${NIVEL_LABEL[nivel].toLowerCase()}`);
        } finally {
          setCreando(false);
        }
      };

      const renderNivel = (nivel, padreId, habilitado) => {
        const opciones = habilitado ? hijosDe(nivel, padreId) : [];
        return (
          <div key={nivel}>
            <label className="block text-xs text-gray-600 mb-1">{NIVEL_LABEL[nivel]}</label>
            <div className="flex gap-1">
              <select
                value={sel[nivel] || ''}
                onChange={(e) => seleccionar(nivel, e.target.value)}
                disabled={disabled || !habilitado}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
              >
                <option value="">{habilitado ? 'Selecciona...' : '—'}</option>
                {opciones.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
              </select>
              {!disabled && habilitado && (
                <button
                  type="button"
                  onClick={() => { setNuevoNivel(nuevoNivel === nivel ? null : nivel); setNuevoNombre(''); }}
                  className="px-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50"
                  title={`Nuevo/a ${NIVEL_LABEL[nivel].toLowerCase()}`}
                >
                  <Plus size={14} />
                </button>
              )}
            </div>
            {nuevoNivel === nivel && (
              <div className="flex gap-1 mt-1">
                <input
                  type="text"
                  value={nuevoNombre}
                  onChange={(e) => setNuevoNombre(e.target.value)}
                  placeholder={`Nombre de ${NIVEL_LABEL[nivel].toLowerCase()}`}
                  className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  disabled={creando || !nuevoNombre.trim()}
                  onClick={() => crearAqui(nivel, padreId)}
                  className="px-2 py-1 bg-blue-600 text-white rounded-lg text-xs disabled:bg-gray-300"
                >
                  OK
                </button>
              </div>
            )}
          </div>
        );
      };

      return (
        <div>
          <label className="block text-gray-700 font-medium mb-2">Ubicación en Almacén</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {renderNivel('almacen', null, true)}
            {renderNivel('zona', sel.almacen, !!sel.almacen)}
            {renderNivel('area', sel.zona, !!sel.zona)}
            {renderNivel('rack', sel.area, !!sel.area)}
          </div>
        </div>
      );
    };

    const ArticuloModal = ({ mode, articulo, onClose, onSuccess, showFeedback }) => {
      const [formData, setFormData] = useState({
        tipo: 'articulo',
        tiene_variantes: false,
        producto_padre_id: '',
        atributos_variante: null,
        nombre: '',
        marca: '',
        slug: '',
        descripcion: '',
        meta_descripcion: '',
        imagen: '',
        proveedor_id: '',
        categoria_id: '',
        precio_costo: '',
        precio_venta: '',
        precio_venta_sin_descuento: '',
        porcentaje_ganancia: '',
        tasa_impuesto: '',
        precio_mayorista: '',
        stock_actual: '',
        stock_minimo: '',
        unidad_medida: 'unidad',
        ubicacion_id: null,
        peso: '',
        peso_unidad: 'kg',
        largo: '',
        ancho: '',
        alto: '',
        dimensiones_unidad: 'cm',
        url_proveedor: '',
        tiempo_entrega: '',
        fecha_eta: '',
        fecha_vencimiento: '',
        marketplace_amazon: false,
        marketplace_mercadolibre: false,
        marketplace_ebay: false,
        marketplace_google: false,
        costo_servicio_express_1: '',
        costo_servicio_express_2: '',
        costo_servicio_express_3: '',
        costo_servicio_express_4: '',
        tags: '',
        notas: '',
        certificaciones: null,
        por_comprar: false,
        es_destacado: false,
        es_nuevo: false,
        en_oferta: false,
        activo: true
      });
    
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState('');
      const [proveedores, setProveedores] = useState([]);
      const [categorias, setCategorias] = useState([]);
      const [ubicaciones, setUbicaciones] = useState([]);
      const [showNuevaCategoria, setShowNuevaCategoria] = useState(false);
      const [nuevaCategoria, setNuevaCategoria] = useState({
        nombre: '',
        descripcion: '',
        color: '#3B82F6'
      });
    
      // ⭐ ESTADOS PARA GALERÍA DE IMÁGENES ⭐
      const [imagenes, setImagenes] = useState([]);
      const [uploadingImage, setUploadingImage] = useState(false);
      const [selectedImage, setSelectedImage] = useState(null);
      const [showImageModal, setShowImageModal] = useState(false);
      const [imagenesPendientes, setImagenesPendientes] = useState([]); // ⭐ NUEVO
      const [previews, setPreviews] = useState([]); 
      const [showProveedorModal, setShowProveedorModal] = useState(false);
      const [proveedorDetalles, setProveedorDetalles] = useState(null);
      const [loadingProveedor, setLoadingProveedor] = useState(false);
      const [activeTab, setActiveTab] = useState('general');

      // Cargar proveedores y categorías
      useEffect(() => {
        const cargarDatos = async () => {
          try {
            const [respProveedores, respCategorias, respUbicaciones] = await Promise.all([
              proveedoresService.getAll({ activo: true }),
              categoriasService.getActivas(),
              ubicacionesAlmacenService.getArbol()
            ]);
            setProveedores(respProveedores.data);
            setCategorias(respCategorias.data);
            setUbicaciones(respUbicaciones.data);
          } catch (error) {
            console.error('Error al cargar datos:', error);
          }
        };
    
        cargarDatos();
      }, []);
    
      // Cargar datos del artículo si es edición o vista
      useEffect(() => {
        if (articulo && (mode === 'edit' || mode === 'view')) {
          setFormData({
            tipo: articulo.tipo || 'articulo',
            tiene_variantes: articulo.tiene_variantes || false,
            producto_padre_id: articulo.producto_padre_id || '',
            atributos_variante: articulo.atributos_variante || null,
            nombre: articulo.nombre || '',
            marca: articulo.marca || '',
            slug: articulo.slug || '',
            descripcion: articulo.descripcion || '',
            meta_descripcion: articulo.meta_descripcion || '',
            proveedor_id: articulo.proveedor_id || '',
            categoria_id: articulo.categoria_id || '',
            precio_costo: articulo.precio_costo || '',
            precio_venta: articulo.precio_venta || '',
            precio_venta_sin_descuento: articulo.precio_venta_sin_descuento || '',
            porcentaje_ganancia: articulo.porcentaje_ganancia || '',
            tasa_impuesto: articulo.tasa_impuesto || '',
            precio_mayorista: articulo.precio_mayorista || '',
            stock_actual: articulo.stock_actual || '',
            stock_minimo: articulo.stock_minimo || '',
            unidad_medida: articulo.unidad_medida || 'unidad',
            ubicacion_id: articulo.ubicacion_id || null,
            peso: articulo.peso || '',
            peso_unidad: articulo.peso_unidad || 'kg',
            largo: articulo.largo || '',
            ancho: articulo.ancho || '',
            alto: articulo.alto || '',
            dimensiones_unidad: articulo.dimensiones_unidad || 'cm',
            url_proveedor: articulo.url_proveedor || '',
            tiempo_entrega: articulo.tiempo_entrega || '',
            fecha_eta: articulo.fecha_eta || '',
            fecha_vencimiento: articulo.fecha_vencimiento || '',
            marketplace_amazon: articulo.marketplace_amazon || false,
            marketplace_mercadolibre: articulo.marketplace_mercadolibre || false,
            marketplace_ebay: articulo.marketplace_ebay || false,
            marketplace_google: articulo.marketplace_google || false,
            costo_servicio_express_1: articulo.costo_servicio_express_1 || '',
            costo_servicio_express_2: articulo.costo_servicio_express_2 || '',
            costo_servicio_express_3: articulo.costo_servicio_express_3 || '',
            costo_servicio_express_4: articulo.costo_servicio_express_4 || '',
            tags: articulo.tags || '',
            notas: articulo.notas || '',
            certificaciones: articulo.certificaciones || null,
            por_comprar: articulo.por_comprar || false,
            es_destacado: articulo.es_destacado || false,
            es_nuevo: articulo.es_nuevo || false,
            en_oferta: articulo.en_oferta || false,
            activo: articulo.activo
          });
    
          cargarImagenes(articulo.id);
        }
      }, [articulo, mode]);
      // ⭐ CARGAR IMÁGENES ⭐
      const cargarImagenes = async (articuloId) => {
        try {
          const response = await articulosService.getImagenes(articuloId);
          setImagenes(response.data);
        } catch (error) {
          console.error('Error al cargar imágenes:', error);
        }
      };
    
    
      
    
    // ⭐ SUBIR MÚLTIPLES IMÁGENES CON VALIDACIÓN DE MÁXIMO 4 ⭐
      const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
    
        // ⭐ MODO CREAR: Guardar imágenes para subir después ⭐
        if (mode === 'create') {
          const imagenesActuales = imagenesPendientes.length;
          const imagenesNuevas = files.length;
          const totalImagenes = imagenesActuales + imagenesNuevas;
    
          if (totalImagenes > 4) {
            showFeedback('warning', 'Límite de imágenes', `Solo puedes agregar un máximo de 4 imágenes. Ya tienes ${imagenesActuales} seleccionada(s).`);
            return;
          }
    
          // Validar archivos
          for (let file of files) {
            if (file.size > 5 * 1024 * 1024) {
              showFeedback('warning', 'Archivo muy grande', `La imagen ${file.name} supera los 5MB`);
              continue;
            }
    
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
              showFeedback('warning', 'Formato inválido', `${file.name} no es un formato válido`);
              continue;
            }
          }
    
          // Agregar a pendientes
          setImagenesPendientes(prev => [...prev, ...files]);
    
          // Crear vistas previas
          const newPreviews = await Promise.all(
            files.map(file => {
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(file);
              });
            })
          );
    
          setPreviews(prev => [...prev, ...newPreviews]);
          e.target.value = '';
          return;
        }
    
        // ⭐ MODO EDITAR: Subir inmediatamente ⭐
        if (!articulo || !articulo.id) {
          showFeedback('error', 'Error', 'Error: No se encontró el ID del artículo');
          return;
        }
    
        const imagenesActuales = imagenes.length;
        const imagenesNuevas = files.length;
        const totalImagenes = imagenesActuales + imagenesNuevas;
    
        if (totalImagenes > 4) {
          showFeedback('warning', 'Límite de imágenes', `Solo puedes tener un máximo de 4 imágenes por artículo. Actualmente tienes ${imagenesActuales} imagen(es). Puedes agregar ${4 - imagenesActuales} más.`);
          return;
        }
    
        setUploadingImage(true);
        
        try {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
    
            if (file.size > 5 * 1024 * 1024) {
              showFeedback('warning', 'Imagen muy grande', `La imagen ${file.name} supera los 5MB`);
              continue;
            }

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
              showFeedback('warning', 'Formato inválido', `${file.name} no es un formato válido`);
              continue;
            }
    
            const esPrincipal = imagenes.length === 0 && i === 0;
            await articulosService.addImagen(articulo.id, file, esPrincipal);
          }
    
          showFeedback('success', 'Éxito', 'Imágenes subidas exitosamente');
          await cargarImagenes(articulo.id);
          e.target.value = '';
        } catch (error) {
          console.error('Error al subir imágenes:', error);
          showFeedback('error', 'Error', error.message || 'Error al subir imágenes');
        } finally {
          setUploadingImage(false);
        }
      };
    
    
      // ⭐ ELIMINAR IMAGEN PENDIENTE (MODO CREAR) ⭐
      const handleDeletePendiente = (index) => {
        setImagenesPendientes(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));
      };  
      const handleDeleteImagen = (imagenId) => {
        showFeedback('warning', 'Confirmar Eliminación', '¿Estás seguro de eliminar esta imagen?', async () => {
            try {
              await articulosService.deleteImagen(articulo.id, imagenId);
              showFeedback('success', 'Éxito', 'Imagen eliminada exitosamente');
              await cargarImagenes(articulo.id);
            } catch (error) {
              console.error('Error al eliminar imagen:', error);
              showFeedback('error', 'Error', error.message || 'Error al eliminar imagen');
            }
        }, true);
      };
    
      // ⭐ ESTABLECER COMO PRINCIPAL ⭐
      const handleSetPrincipal = async (imagenId) => {
        try {
          await articulosService.setPrincipalImagen(articulo.id, imagenId);
          showFeedback('success', 'Éxito', 'Imagen establecida como principal');
          await cargarImagenes(articulo.id);
        } catch (error) {
          console.error('Error al establecer imagen principal:', error);
          showFeedback('error', 'Error', error.message || 'Error al establecer imagen principal');
        }
      };
    
      // ⭐ VER IMAGEN EN GRANDE ⭐
      const handleViewImage = (imagen) => {
        setSelectedImage(imagen);
        setShowImageModal(true);
      };
    
      const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
      };
    
       // ⭐ FUNCIÓN PARA VER DETALLES DEL PROVEEDOR ⭐
      const handleVerProveedorDetalles = async (proveedorId) => {
        try {
          setLoadingProveedor(true);
          const response = await proveedoresService.getById(proveedorId);
          setProveedorDetalles(response.data);
          setShowProveedorModal(true);
        } catch (error) {
          console.error('Error al cargar proveedor:', error);
          showFeedback('error', 'Error', 'Error al cargar los detalles del proveedor');
        } finally {
          setLoadingProveedor(false);
        }
      };
    
    
      // Calcular porcentaje de ganancia automáticamente
      useEffect(() => {
        if (formData.precio_costo && formData.precio_venta) {
          const costo = parseFloat(formData.precio_costo);
          const venta = parseFloat(formData.precio_venta);
          if (costo > 0 && venta > 0) {
            const ganancia = ((venta - costo) / costo) * 100;
            setFormData(prev => ({
              ...prev,
              porcentaje_ganancia: ganancia.toFixed(2)
            }));
          }
        }
      }, [formData.precio_costo, formData.precio_venta]);
    
      const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
    
        try {
          let articuloCreado = null;
    
          if (mode === 'create') {
            const response = await articulosService.create(formData);
            articuloCreado = response.data;
            
            // ⭐ Si hay imágenes pendientes, subirlas ahora ⭐
            if (imagenesPendientes.length > 0) {
              setUploadingImage(true);
              try {
                for (let i = 0; i < imagenesPendientes.length; i++) {
                  const esPrincipal = i === 0; // Primera imagen es principal
                  await articulosService.addImagen(articuloCreado.id, imagenesPendientes[i], esPrincipal);
                }
                onSuccess('Artículo e imágenes guardados exitosamente');
              } catch (imgError) {
                console.error('Error al subir imágenes:', imgError);
                showFeedback('warning', 'Advertencia', 'Artículo creado pero hubo un error al subir algunas imágenes');
                onSuccess();
              } finally {
                setUploadingImage(false);
                setImagenesPendientes([]);
              }
            } else {
              onSuccess('Artículo/Servicio creado exitosamente');
            }
          } else if (mode === 'edit') {
            await articulosService.update(articulo.id, formData);
            onSuccess('Artículo/Servicio actualizado exitosamente');
          }
        } catch (err) {
          setError(err.message || 'Error al guardar artículo/servicio');
        } finally {
          setLoading(false);
        }
      };
    
      const handleCrearCategoria = async () => {
        if (!nuevaCategoria.nombre.trim()) {
          showFeedback('warning', 'Datos incompletos', 'El nombre de la categoría es requerido');
          return;
        }
    
        try {
          const response = await categoriasService.create(nuevaCategoria);
          showFeedback('success', 'Éxito', 'Categoría creada exitosamente');
          
          const respCategorias = await categoriasService.getActivas();
          setCategorias(respCategorias.data);
          
          setFormData(prev => ({
            ...prev,
            categoria_id: response.data.id
          }));
          
          setNuevaCategoria({ nombre: '', descripcion: '', color: '#3B82F6' });
          setShowNuevaCategoria(false);
        } catch (error) {
          showFeedback('error', 'Error', error.message || 'Error al crear categoría');
        }
      };
    
      const isViewMode = mode === 'view';
    
      const coloresPredefinidos = [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
        '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
      ];
    
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">
                  {mode === 'create' && 'Nuevo Artículo/Servicio'}
                  {mode === 'edit' && 'Editar Artículo/Servicio'}
                  {mode === 'view' && 'Detalles del Artículo/Servicio'}
                </h3>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="flex gap-1 px-6 pt-3 border-b border-gray-200 bg-white sticky top-[73px] z-10 overflow-x-auto">
              {[
                { id: 'general', label: 'General', icon: Package },
                { id: 'precios', label: 'Precios', icon: DollarSign },
                { id: 'stock', label: 'Stock y Logística', icon: Boxes },
                { id: 'compra', label: 'Compra y Envíos', icon: ShoppingCart },
                ...(isViewMode ? [{ id: 'sistema', label: 'Sistema', icon: Settings }] : [])
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
    
              <div className="space-y-6">
                {activeTab === 'general' && (
                <>
                {/* SECCIÓN 1: Información Básica */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Package size={20} />
                    Información Básica
                  </h4>
    
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tipo */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Tipo <span className="text-red-500">*</span>
                      </label>
                            <select
                              name="tipo"
                              value={formData.tipo}
                              onChange={handleChange}
                              disabled={isViewMode}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            >
                              <option value="articulo">Artículo</option>
                              <option value="servicio">Servicio</option>
                              <option value="por_cotizar">Por Cotizar</option> {/* ✅ CORREGIDO */}
                            </select>
                    </div>
    
                    {/* Estado Activo */}
                    {mode === 'edit' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="activo"
                          checked={formData.activo}
                          onChange={handleChange}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label className="ml-2 text-gray-700 font-medium">Activo</label>
                      </div>
                    )}
                  </div>
    
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    {/* Nombre */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        disabled={isViewMode}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Nombre del artículo o servicio"
                      />
                    </div>
    
                    {/* Descripción */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Descripción</label>
                      <textarea
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={handleChange}
                        disabled={isViewMode}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Descripción detallada"
                      />
                    </div>
       
                 {/* ⭐ GALERÍA DE IMÁGENES ⭐ */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Galería de Imágenes {mode === 'create' && '(se subirán al guardar)'}
                      </label>
    
                      {!isViewMode && (
                        <div className="mb-4">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Puedes seleccionar múltiples imágenes. Formatos: JPG, PNG, GIF, WEBP (máximo 5MB cada una, máximo 4 imágenes)
                          </p>
                        </div>
                      )}
    
                      {uploadingImage && (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700">⏳ Subiendo imágenes...</p>
                        </div>
                      )}
    
                      {/* ⭐ MODO CREAR: Mostrar vistas previas ⭐ */}
                      {mode === 'create' && previews.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          {previews.map((preview, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={preview}
                                alt={`Vista previa ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-300"
                              />
                              {index === 0 && (
                                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                  Principal
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeletePendiente(index)}
                                className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </button>
                              <p className="text-xs text-gray-600 mt-1 truncate">
                                {imagenesPendientes[index]?.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
    
                      {/* ⭐ MODO EDITAR/VER: Mostrar imágenes guardadas ⭐ */}
                      {(mode === 'edit' || mode === 'view') && imagenes.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          {imagenes.map((img) => (
                            <div key={img.id} className="relative group">
                              <img
                                src={`${UPLOADS_BASE_URL}${img.ruta_imagen}`}
                                alt={img.nombre_original}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 transition"
                                onClick={() => handleViewImage(img)}
                              />
                              {img.es_principal && (
                                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                  Principal
                                </span>
                              )}
                              {!isViewMode && (
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                                  {!img.es_principal && (
                                    <button
                                      type="button"
                                      onClick={() => handleSetPrincipal(img.id)}
                                      className="bg-blue-600 text-white p-1 rounded hover:bg-blue-700"
                                      title="Establecer como principal"
                                    >
                                      <CheckCircle size={16} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteImagen(img.id)}
                                    className="bg-red-600 text-white p-1 rounded hover:bg-red-700"
                                    title="Eliminar"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
    
                      {/* Sin imágenes */}
                      {mode !== 'create' && imagenes.length === 0 && previews.length === 0 && (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <Package size={48} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500 text-sm">
                            No hay imágenes. {!isViewMode && 'Agrega algunas usando el botón de arriba.'}
                          </p>
                        </div>
                      )}
    
                      {mode === 'create' && previews.length === 0 && (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <Package size={48} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500 text-sm">
                            Selecciona imágenes para agregar al artículo
                          </p>
                        </div>
                      )}
                    </div>
    
                    {/* Mensaje para modo crear
                    {mode === 'create' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-700">
                          💡 Primero guarda el artículo, luego podrás agregar imágenes editándolo.
                        </p>
                      </div>
                    )} */}
                  </div>
                </div>
              {/* SECCIÓN 2: Categoría y Proveedor */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Tag size={20} />
                  Categoría y Proveedor
                </h4>
    
    
    
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Categoría */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-gray-700 font-medium">Categoría</label>
                        {!isViewMode && (
                          <button
                            type="button"
                            onClick={() => setShowNuevaCategoria(!showNuevaCategoria)}
                            className="text-blue-600 text-sm hover:text-blue-800"
                          >
                            {showNuevaCategoria ? 'Cancelar' : '+ Nueva'}
                          </button>
                        )}
                      </div>
    
                      {showNuevaCategoria ? (
                        <div className="border border-blue-300 rounded-lg p-3 bg-blue-50">
                          <input
                            type="text"
                            value={nuevaCategoria.nombre}
                            onChange={(e) => setNuevaCategoria({ ...nuevaCategoria, nombre: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded mb-2"
                            placeholder="Nombre de la categoría"
                          />
                          <input
                            type="text"
                            value={nuevaCategoria.descripcion}
                            onChange={(e) => setNuevaCategoria({ ...nuevaCategoria, descripcion: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded mb-2"
                            placeholder="Descripción (opcional)"
                          />
                          <div className="flex gap-2 mb-2">
                            {coloresPredefinidos.map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => setNuevaCategoria({ ...nuevaCategoria, color })}
                                className={`w-8 h-8 rounded border-2 ${
                                  nuevaCategoria.color === color ? 'border-gray-800' : 'border-gray-300'
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={handleCrearCategoria}
                            className="w-full bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                          >
                            Crear Categoría
                          </button>
                        </div>
                      ) : (
                        <select
                          name="categoria_id"
                          value={formData.categoria_id}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Sin categoría</option>
                          {categorias.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                          ))}
                        </select>
                      )}
                    </div>
    
                    {/* Proveedor */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Proveedor</label>
                      <div className="flex gap-2">
                        <select
                          name="proveedor_id"
                          value={formData.proveedor_id}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Sin proveedor</option>
                          {proveedores.map(prov => (
                            <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                          ))}
                        </select>
                        
                        {/* Botón para ver detalles del proveedor */}
                        {formData.proveedor_id && (
                          <button
                            type="button"
                            onClick={() => handleVerProveedorDetalles(formData.proveedor_id)}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-1"
                            title="Ver detalles del proveedor"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}

                {activeTab === 'precios' && (
                <>
                {/* SECCIÓN 3: Precios */}
                 {/* SECCIÓN: Precios y Costos */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <DollarSign size={20} />
                    Precios y Costos
                  </h4>
    
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Precio Costo</label>
                      <input
                        type="number"
                        step="0.01"
                        name="precio_costo"
                        value={formData.precio_costo}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Precio Venta</label>
                      <input
                        type="number"
                        step="0.01"
                        name="precio_venta"
                        value={formData.precio_venta}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Precio Sin Descuento</label>
                      <input
                        type="number"
                        step="0.01"
                        name="precio_venta_sin_descuento"
                        value={formData.precio_venta_sin_descuento}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">% Ganancia</label>
                      <input
                        type="text"
                        name="porcentaje_ganancia"
                        value={formData.porcentaje_ganancia}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
                        placeholder="Auto"
                      />
                    </div>
                  </div>
    
                  {/* ⭐ NUEVOS CAMPOS DE PRECIO ⭐ */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Precio Mayorista</label>
                      <input
                        type="number"
                        step="0.01"
                        name="precio_mayorista"
                        value={formData.precio_mayorista}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">Para ventas al por mayor</p>
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Tasa de Impuesto (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="tasa_impuesto"
                        value={formData.tasa_impuesto}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">IVA u otro impuesto</p>
                    </div>
                  </div>
                </div>

                <PreciosProveedoresSection articuloId={articulo?.id} proveedores={proveedores} isViewMode={isViewMode} showFeedback={showFeedback} />
                </>
                )}

                {activeTab === 'stock' && (
                <>
                {/* SECCIÓN 4: Stock (solo para artículos) */}
                {formData.tipo === 'articulo' && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Boxes size={20} />
                      Inventario
                    </h4>
    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Stock Actual</label>
                        <input
                          type="number"
                          name="stock_actual"
                          value={formData.stock_actual}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          placeholder="0"
                        />
                      </div>
    
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Stock Mínimo</label>
                        <input
                          type="number"
                          name="stock_minimo"
                          value={formData.stock_minimo}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          placeholder="0"
                        />
                      </div>
    
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Unidad de Medida</label>
                        <select
                          name="unidad_medida"
                          value={formData.unidad_medida}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="unidad">Unidad</option>
                          <option value="caja">Caja</option>
                          <option value="paquete">Paquete</option>
                          <option value="metro">Metro</option>
                          <option value="litro">Litro</option>
                          <option value="kilogramo">Kilogramo</option>
                        </select>
                      </div>
    
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="por_comprar"
                          checked={formData.por_comprar}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label className="ml-2 text-gray-700 font-medium">Por Comprar</label>
                      </div>
                    </div>
    
                    {/* ⭐ NUEVOS CAMPOS: DIMENSIONES ⭐ */}
                    <div className="col-span-full mt-4">
                      <label className="block text-gray-700 font-medium mb-2">Dimensiones (Logística)</label>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <input
                            type="number"
                            step="0.01"
                            name="largo"
                            value={formData.largo}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            placeholder="Largo"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            step="0.01"
                            name="ancho"
                            value={formData.ancho}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            placeholder="Ancho"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            step="0.01"
                            name="alto"
                            value={formData.alto}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            placeholder="Alto"
                          />
                        </div>
                        <div>
                          <select
                            name="dimensiones_unidad"
                            value={formData.dimensiones_unidad}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          >
                            <option value="cm">cm</option>
                            <option value="m">m</option>
                            <option value="in">in</option>
                            <option value="ft">ft</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Dimensiones para cálculo de envíos y espacio</p>
                    </div>
    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Peso</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            step="0.01"
                            name="peso"
                            value={formData.peso}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                            placeholder="0.00"
                          />
                          <select
                            name="peso_unidad"
                            value={formData.peso_unidad}
                            onChange={handleChange}
                            disabled={isViewMode}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          >
                            <option value="kg">kg</option>
                            <option value="g">g</option>
                            <option value="lb">lb</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
    
    
                {/* SECCIÓN: INFORMACIÓN DEL PRODUCTO */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Tag size={20} />
                    Información del Producto
                  </h4>
    
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Marca */}
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Marca</label>
                      <input
                        type="text"
                        name="marca"
                        value={formData.marca}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Ej: Samsung, Nike, Sony"
                      />
                    </div>
    
                    {/* Ubicación en Almacén */}
                    <div className="md:col-span-2">
                      <UbicacionSelector
                        arbol={ubicaciones}
                        ubicacionId={formData.ubicacion_id}
                        onChange={(id) => setFormData(prev => ({ ...prev, ubicacion_id: id }))}
                        onArbolChange={(updater) => setUbicaciones(updater)}
                        disabled={isViewMode}
                        showFeedback={showFeedback}
                      />
                    </div>
    
                    {/* Fecha de Vencimiento */}
                    {formData.tipo === 'articulo' && (
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Fecha de Vencimiento</label>
                        <input
                          type="date"
                          name="fecha_vencimiento"
                          value={formData.fecha_vencimiento}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    )}
                  </div>
                </div>
                </>
                )}

                {activeTab === 'compra' && (
                <>
                {/* SECCIÓN 5: Información de Compra */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <ShoppingCart size={20} />
                    Información de Compra
                  </h4>
    
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">URL del Proveedor</label>
                      <input
                        type="url"
                        name="url_proveedor"
                        value={formData.url_proveedor}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="https://..."
                      />
                    </div>
    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Tiempo de Entrega</label>
                        <input
                          type="text"
                          name="tiempo_entrega"
                          value={formData.tiempo_entrega}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                          placeholder="Ej: 5-7 días"
                        />
                      </div>
    
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Fecha ETA</label>
                        <input
                          type="date"
                          name="fecha_eta"
                          value={formData.fecha_eta}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
    
                {/* SECCIÓN 6: Marketplaces */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4">Marketplaces</h4>
    
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="marketplace_amazon"
                        checked={formData.marketplace_amazon}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-gray-700">Amazon</label>
                    </div>
    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="marketplace_mercadolibre"
                        checked={formData.marketplace_mercadolibre}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-gray-700">MercadoLibre</label>
                    </div>
    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="marketplace_ebay"
                        checked={formData.marketplace_ebay}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-gray-700">eBay</label>
                    </div>
    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="marketplace_google"
                        checked={formData.marketplace_google}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label className="ml-2 text-gray-700">Google</label>
                    </div>
                  </div>
                </div>
    
                {/* SECCIÓN 7: Costos de Servicios Express */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4">Costos de Servicios Express</h4>
    
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">C.E Servicios Expresos (AEREO BCV)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="costo_servicio_express_1"
                        value={formData.costo_servicio_express_1}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
             
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">C.E Servicios Expresos (MARITIMO BCV)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="costo_servicio_express_2"
                        value={formData.costo_servicio_express_2}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">C.E Servicios Expresos (AEREO BINANCE)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="costo_servicio_express_3"
                        value={formData.costo_servicio_express_3}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">C.E Servicios Expresos (MARITIMO)	</label>
                      <input
                        type="number"
                        step="0.01"
                        name="costo_servicio_express_4"
                        value={formData.costo_servicio_express_4}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>


                  </div>
                </div>
                </>
                )}

                {activeTab === 'general' && (
                <>
                {/* SECCIÓN 8: Tags y Notas */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-4">Información Adicional</h4>
    
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Tags <span className="text-xs text-gray-500">(separados por coma)</span>
                      </label>
                      <input
                        type="text"
                        name="tags"
                        value={formData.tags}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Ej: electrónica, importado, oferta"
                      />
                    </div>
    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Notas</label>
                      <textarea
                        name="notas"
                        value={formData.notas}
                        onChange={handleChange}
                        disabled={isViewMode}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Notas adicionales sobre el artículo o servicio"
                      />
                    </div>
                  </div>
                </div>
                </>
                )}

                {/* <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Star size={20} className="text-purple-600" />
                    SEO y Marketing
                  </h4>
    
                  <div className="grid grid-cols-1 gap-4">
                
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        URL Amigable (Slug)
                      </label>
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        disabled={isViewMode}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                        placeholder="producto-ejemplo-slug"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Para URLs: ejemplo.com/productos/{formData.slug || 'slug'}
                      </p>
                    </div>
    
          
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Meta Descripción (SEO)
                      </label>
                      <textarea
                        name="meta_descripcion"
                        value={formData.meta_descripcion}
                        onChange={handleChange}
                        disabled={isViewMode}
                        rows="2"
                        maxLength="160"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                        placeholder="Descripción breve para motores de búsqueda (máximo 160 caracteres)"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.meta_descripcion?.length || 0}/160 caracteres
                      </p>
                    </div>
    
            
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center p-3 bg-white rounded-lg border border-gray-300">
                        <input
                          type="checkbox"
                          name="es_destacado"
                          checked={formData.es_destacado}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label className="ml-2 text-gray-700 font-medium flex items-center gap-1">
                          <Star size={16} className="text-yellow-500" />
                          Destacado
                        </label>
                      </div>
    
                      <div className="flex items-center p-3 bg-white rounded-lg border border-gray-300">
                        <input
                          type="checkbox"
                          name="es_nuevo"
                          checked={formData.es_nuevo}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <label className="ml-2 text-gray-700 font-medium flex items-center gap-1">
                          <Sparkles size={16} className="text-green-500" />
                          Nuevo
                        </label>
                      </div>
    
                      <div className="flex items-center p-3 bg-white rounded-lg border border-gray-300">
                        <input
                          type="checkbox"
                          name="en_oferta"
                          checked={formData.en_oferta}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <label className="ml-2 text-gray-700 font-medium flex items-center gap-1">
                          <Percent size={16} className="text-red-500" />
                          En Oferta
                        </label>
                      </div>
                    </div>
                  </div>
                </div> */}
    
                {/* Información de auditoría */}
                {isViewMode && articulo && activeTab === 'sistema' && (
                  <div className="bg-gray-50 p-4 rounded-lg border-t-4 border-blue-500">
                    <h4 className="font-semibold text-gray-800 mb-4">Información del Sistema</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">ID:</p>
                        <p className="font-semibold">{articulo.id}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Creado por:</p>
                        <p className="font-semibold">{articulo.creador_nombre || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Fecha de creación:</p>
                        <p className="font-semibold">
                          {new Date(articulo.created_at).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
    
            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-4 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isViewMode ? 'Cerrar' : 'Cancelar'}
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={loading || uploadingImage}
                  className={`px-6 py-2 rounded-lg text-white ${
                    loading || uploadingImage
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              )}
            </div>
          </div>
    
          {/* ⭐ MODAL PARA VER IMAGEN EN GRANDE ⭐ */}
          {showImageModal && selectedImage && (
            <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[60]" onClick={() => setShowImageModal(false)}>
              <div className="max-w-4xl max-h-[90vh] relative">
                <button
                  onClick={() => setShowImageModal(false)}
                  className="absolute -top-10 right-0 text-white hover:text-gray-300"
                >
                  <span className="text-3xl">×</span>
                </button>
                <img
                  src={`${UPLOADS_BASE_URL}${selectedImage.ruta_imagen}`}
                  alt={selectedImage.nombre_original}
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
                <p className="text-white text-center mt-4">{selectedImage.nombre_original}</p>
              </div>
            </div>
          )}
    
          {/* ⭐ MODAL DE DETALLES DEL PROVEEDOR ⭐ */}
          {showProveedorModal && proveedorDetalles && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={() => setShowProveedorModal(false)}>
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Building2 size={28} className="text-blue-600" />
                        {proveedorDetalles.nombre}
                      </h3>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                        proveedorDetalles.tipo_persona === 'natural' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {proveedorDetalles.tipo_persona === 'natural' ? 'Persona Natural' : 'Persona Jurídica'}
                      </span>
                    </div>
                    <button 
                      onClick={() => setShowProveedorModal(false)}
                      className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                </div>
    
                {/* Contenido */}
                <div className="p-6 space-y-6">
                  {/* Información de Contacto */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <User size={18} />
                      Información de Contacto
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-start gap-2">
                        <User size={16} className="text-gray-500 mt-1" />
                        <div>
                          <p className="text-gray-600">Identificación:</p>
                          <p className="font-semibold">
                            {proveedorDetalles.tipo_persona === 'natural' 
                              ? proveedorDetalles.cedula 
                              : proveedorDetalles.rif}
                          </p>
                        </div>
                      </div>
                      {proveedorDetalles.telefono && (
                        <div className="flex items-start gap-2">
                          <Phone size={16} className="text-gray-500 mt-1" />
                          <div>
                            <p className="text-gray-600">Teléfono:</p>
                            <p className="font-semibold">{proveedorDetalles.telefono}</p>
                          </div>
                        </div>
                      )}
                      {proveedorDetalles.email && (
                        <div className="flex items-start gap-2">
                          <Mail size={16} className="text-gray-500 mt-1" />
                          <div>
                            <p className="text-gray-600">Email:</p>
                            <p className="font-semibold">{proveedorDetalles.email}</p>
                          </div>
                        </div>
                      )}
                      {proveedorDetalles.direccion && (
                        <div className="flex items-start gap-2 md:col-span-2">
                          <MapPin size={16} className="text-gray-500 mt-1" />
                          <div>
                            <p className="text-gray-600">Dirección:</p>
                            <p className="font-semibold">{proveedorDetalles.direccion}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
    
                  {/* Cuentas Bancarias */}
                  {proveedorDetalles.cuentas_bancarias && proveedorDetalles.cuentas_bancarias.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <CreditCard size={18} />
                        Cuentas Bancarias ({proveedorDetalles.cuentas_bancarias.length})
                      </h4>
                      <div className="space-y-3">
                        {proveedorDetalles.cuentas_bancarias.map((cuenta) => (
                          <div key={cuenta.id} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <CreditCard className="text-blue-600" size={18} />
                                <h5 className="font-semibold text-gray-800">{cuenta.banco_nombre}</h5>
                              </div>
                              {cuenta.principal && (
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                  Principal
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-gray-600">Número:</p>
                                <p className="font-mono font-semibold">{cuenta.numero_cuenta}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Tipo:</p>
                                <p className="font-semibold capitalize">{cuenta.tipo_cuenta}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Titular:</p>
                                <p className="font-semibold">{cuenta.titular_cuenta}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Cédula:</p>
                                <p className="font-semibold">{cuenta.cedula_titular}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const texto = `DATOS BANCARIOS\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n🏦 Banco: ${cuenta.banco_nombre}\n💳 Número: ${cuenta.numero_cuenta}\n📋 Tipo: ${cuenta.tipo_cuenta}\n👤 Titular: ${cuenta.titular_cuenta}\n🆔 Cédula: ${cuenta.cedula_titular}`;
                                copiarAlPortapapeles(texto, 'Datos bancarios copiados', showFeedback);
                              }}
                              className="mt-2 w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm flex items-center justify-center gap-2 transition"
                            >
                              <Copy size={14} />
                              Copiar datos
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
    
                  {/* Pagos Móviles */}
                  {proveedorDetalles.pagos_movil && proveedorDetalles.pagos_movil.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <Smartphone size={18} />
                        Pago Móvil ({proveedorDetalles.pagos_movil.length})
                      </h4>
                      <div className="space-y-3">
                        {proveedorDetalles.pagos_movil.map((pago) => (
                          <div key={pago.id} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2">
                                <Smartphone className="text-green-600" size={18} />
                                <h5 className="font-semibold text-gray-800">{pago.banco_nombre}</h5>
                              </div>
                              {pago.principal && (
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                  Principal
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-gray-600">Teléfono:</p>
                                <p className="font-mono font-semibold">{pago.telefono}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Cédula:</p>
                                <p className="font-semibold">{pago.cedula}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-gray-600">Titular:</p>
                                <p className="font-semibold">{pago.titular}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const texto = `PAGO MÓVIL\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n🏦 Banco: ${pago.banco_nombre}\n📱 Teléfono: ${pago.telefono}\n🆔 Cédula: ${pago.cedula}\n👤 Titular: ${pago.titular}`;
                                copiarAlPortapapeles(texto, 'Datos de pago móvil copiados', showFeedback);
                              }}
                              className="mt-2 w-full bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1 rounded text-sm flex items-center justify-center gap-2 transition"
                            >
                              <Copy size={14} />
                              Copiar datos
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
    
                  {/* Notas */}
                  {proveedorDetalles.notas && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <h4 className="font-semibold text-gray-800 mb-2">Notas:</h4>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{proveedorDetalles.notas}</p>
                    </div>
                  )}
    
                  {/* Sin métodos de pago */}
                  {(!proveedorDetalles.cuentas_bancarias || proveedorDetalles.cuentas_bancarias.length === 0) &&
                   (!proveedorDetalles.pagos_movil || proveedorDetalles.pagos_movil.length === 0) && (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <AlertCircle size={48} className="mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">Este proveedor no tiene métodos de pago registrados</p>
                    </div>
                  )}
                </div>
    
                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
                  <button
                    onClick={() => setShowProveedorModal(false)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    const GaleriaModal = ({ articulo, imagenes, onClose }) => {
      const [imagenActual, setImagenActual] = useState(imagenes.find(img => img.es_principal) || imagenes[0]);
    
      if (!articulo || imagenes.length === 0) {
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Galería de {articulo?.nombre}</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <span className="text-2xl">×</span>
                </button>
              </div>
              <div className="text-center py-12">
                <Package size={64} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Este artículo no tiene imágenes</p>
              </div>
            </div>
          </div>
        );
      }
    
      return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50" onClick={onClose}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-xl font-bold text-gray-800">{articulo.nombre}</h3>
                <p className="text-sm text-gray-600">{imagenes.length} imagen(es)</p>
              </div>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
    
            {/* Contenido */}
            <div className="flex flex-col md:flex-row h-[calc(90vh-80px)]">
              {/* Imagen principal */}
              <div className="flex-1 bg-gray-900 flex items-center justify-center p-4">
                <div className="relative max-w-full max-h-full">
                  <img
                    src={`${UPLOADS_BASE_URL}${imagenActual.ruta_imagen}`}
                    alt={imagenActual.nombre_original}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  />
                  {imagenActual.es_principal && (
                    <span className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Principal
                    </span>
                  )}
                </div>
              </div>
    
              {/* Miniaturas laterales */}
              <div className="w-full md:w-48 bg-gray-50 p-4 overflow-y-auto">
                <p className="text-sm font-semibold text-gray-700 mb-3">Todas las imágenes</p>
                <div className="space-y-3">
                  {imagenes.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => setImagenActual(img)}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition ${
                        imagenActual.id === img.id
                          ? 'border-blue-500 shadow-lg'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <img
                        src={`${UPLOADS_BASE_URL}${img.ruta_imagen}`}
                        alt={img.nombre_original}
                        className="w-full h-32 object-cover"
                      />
                      {img.es_principal === 1 && (
                        <span className="absolute top-1 left-1 bg-green-500 text-white px-2 py-0.5 rounded text-xs">
                          Principal
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
    
            {/* Footer con info */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Archivo:</span> {imagenActual.nombre_original}
              </p>
            </div>
          </div>
        </div>
      );
    };

    const ArticulosView = () => {
    const { articuloFilterId, setArticuloFilterId } = useApp();
    const [articulos, setArticulos] = useState([]);
    const [articulosFiltrados, setArticulosFiltrados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
    const [stats, setStats] = useState(null);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [categoriaFiltro, setCategoriaFiltro] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState('nombre');
    const [sortDirection, setSortDirection] = useState('asc');
    const [expandedCards, setExpandedCards] = useState({});
    const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, showCancel: false });

    const showFeedback = (type, title, message, onConfirm = null, showCancel = false) => {
        setFeedback({ isOpen: true, type, title, message, onConfirm, showCancel });
    };

    const closeFeedback = () => setFeedback(prev => ({ ...prev, isOpen: false }));

    const toggleCardExpansion = (articuloId) => {
        setExpandedCards(prev => ({
            ...prev,
            [articuloId]: !prev[articuloId]
        }));
    };

    // Estados para modal de galería
    const [showGaleriaModal, setShowGaleriaModal] = useState(false);
    const [articuloGaleria, setArticuloGaleria] = useState(null);
    const [imagenesGaleria, setImagenesGaleria] = useState([]);

    const cargarArticulos = async () => {
        try {
        setLoading(true);
        const response = await articulosService.getAll({});
        setArticulos(response.data);
        setArticulosFiltrados(response.data);
        } catch (error) {
        console.error('Error al cargar artículos:', error);
        showFeedback('error', 'Error', error.message || 'Error al cargar artículos');
        } finally {
        setLoading(false);
        }
    };

    const cargarStats = async () => {
        try {
        const response = await articulosService.getStats();
        setStats(response.data);
        } catch (error) {
        console.error('Error al cargar estadísticas:', error);
        }
    };

    useEffect(() => {
        cargarArticulos();
        cargarStats();
    }, []);

    // Llegada desde otro modulo (p.ej. click en un articulo ya cotizado):
    // abre directo el detalle en vez de solo filtrar la lista.
    useEffect(() => {
        if (articuloFilterId && articulos.length > 0) {
            const encontrado = articulos.find(a => a.id === articuloFilterId);
            if (encontrado) {
                setArticuloSeleccionado(encontrado);
                setModalMode('view');
                setShowModal(true);
            }
            setArticuloFilterId(null);
        }
    }, [articuloFilterId, articulos, setArticuloFilterId]);

    useEffect(() => {
        let resultado = [...articulos];

        if (tipoFiltro) {
        resultado = resultado.filter(a => a.tipo === tipoFiltro);
        }

        if (categoriaFiltro) {
        resultado = resultado.filter(a => a.categoria_id == categoriaFiltro);
        }

        if (searchTerm) {
        resultado = resultado.filter(a =>
            a.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (a.codigo && a.codigo.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (a.descripcion && a.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        }

        resultado.sort((a, b) => {
        let aVal = a[sortColumn] || '';
        let bVal = b[sortColumn] || '';
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : -1;
        } else {
            return aVal < bVal ? 1 : -1;
        }
        });

        setArticulosFiltrados(resultado);
        setCurrentPage(1);
    }, [searchTerm, tipoFiltro, categoriaFiltro, articulos, sortColumn, sortDirection]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = articulosFiltrados.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(articulosFiltrados.length / itemsPerPage);

    const handleSort = (column) => {
        if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
        setSortColumn(column);
        setSortDirection('asc');
        }
    };

    const handleNuevoArticulo = () => {
        setModalMode('create');
        setArticuloSeleccionado(null);
        setShowModal(true);
    };

    const handleEditarArticulo = (articulo) => {
        setModalMode('edit');
        setArticuloSeleccionado(articulo);
        setShowModal(true);
    };

    const handleEliminarArticulo = (id) => {
        showFeedback('warning', 'Confirmar Eliminación', '¿Estás seguro de eliminar este artículo/servicio?', async () => {
            try {
                await articulosService.delete(id);
                showFeedback('success', 'Éxito', 'Artículo/Servicio eliminado exitosamente');
                cargarArticulos();
                cargarStats();
            } catch (error) {
                showFeedback('error', 'Error', error.message || 'Error al eliminar artículo');
            }
        }, true);
    };

    const handleVerArticulo = (articulo) => {
        setModalMode('view');
        setArticuloSeleccionado(articulo);
        setShowModal(true);
    };

    const handleModalSuccess = (mensaje) => {
        setShowModal(false);
        setArticuloSeleccionado(null);
        cargarArticulos();
        cargarStats();
        if (mensaje) {
            showFeedback('success', 'Éxito', mensaje);
        }
    };

    const handleVerGaleriaArticulo = async (articulo) => {
        try {
        const response = await articulosService.getImagenes(articulo.id);
        setImagenesGaleria(response.data);
        setArticuloGaleria(articulo);
        setShowGaleriaModal(true);
        } catch (error) {
        console.error('Error al cargar galería:', error);
        showFeedback('error', 'Error', 'Error al cargar la galería de imágenes');
        }
    };

    const SortIcon = ({ column }) => {
        if (sortColumn !== column) return null;
        return sortDirection === 'asc' ? ' ↑' : ' ↓';
    };

    const categoriasUnicas = Array.from(
        new Map(
        articulos
            .filter(a => a.categoria_id && a.categoria_nombre)
            .map(a => [a.categoria_id, { id: a.categoria_id, nombre: a.categoria_nombre }])
        ).values()
    );

    return (
        <div>
        {/* Header responsivo */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Artículos y Servicios</h2>
            <p className="text-sm lg:text-base text-gray-600">Gestiona tu catálogo de productos y servicios</p>
            </div>
            <button 
            onClick={handleNuevoArticulo}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 w-full sm:w-auto"
            >
            <Plus size={20} /> Nuevo Artículo
            </button>
        </div>

        {/* Estadísticas responsivas */}
        {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-3 lg:p-4">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-xs lg:text-sm">Total</p>
                    <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <Package className="text-blue-500" size={24} />
                </div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 lg:p-4">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-xs lg:text-sm">Artículos</p>
                    <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.total_articulos}</p>
                </div>
                <Boxes className="text-green-500" size={24} />
                </div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 lg:p-4">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-xs lg:text-sm">Servicios</p>
                    <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.total_servicios}</p>
                </div>
                <ShoppingBag className="text-purple-500" size={24} />
                </div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 lg:p-4">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-xs lg:text-sm">Activos</p>
                    <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.activos}</p>
                </div>
                <CheckCircle className="text-green-500" size={24} />
                </div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 lg:p-4">
                <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-xs lg:text-sm">Stock Bajo</p>
                    <p className="text-xl lg:text-2xl font-bold text-gray-800">{stats.stock_bajo || 0}</p>
                </div>
                <AlertTriangle className="text-orange-500" size={24} />
                </div>
            </div>
            </div>
        )}

        {/* Filtros responsivos */}
        <div className="bg-white rounded-lg shadow mb-4 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
            <input 
                type="text" 
                placeholder="Buscar..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select 
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
                <option value="">Todos los tipos</option>
                <option value="articulo">Artículos</option>
                <option value="servicio">Servicios</option>
            </select>
            <select 
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
                <option value="">Todas las categorías</option>
                {categoriasUnicas.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
            </select>
            <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
            </select>
            </div>
        </div>

        {/* Layout Unificado - Tarjetas */}
        <div className="space-y-4">
            {loading ? (
            <div className="p-12 text-center bg-white rounded-lg shadow">
                <p className="text-gray-600">Cargando artículos...</p>
            </div>
            ) : articulosFiltrados.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-lg shadow">
                <Package size={64} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron artículos/servicios</p>
            </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {currentItems.map(articulo => (
                        <div key={articulo.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                            {/* Header de la tarjeta */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 rounded-t-lg border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    {/* Imagen pequeña en el header */}
                                    <div className="flex-shrink-0">
                                        {articulo.imagen_principal ? (
                                        <img 
                                            src={`${UPLOADS_BASE_URL}${articulo.imagen_principal}`}
                                            alt={articulo.nombre}
                                            className="w-12 h-12 object-cover rounded border border-gray-300 cursor-pointer"
                                            onClick={() => handleVerGaleriaArticulo(articulo)}
                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                        />
                                        ) : null}
                                        <div 
                                        style={{ display: articulo.imagen_principal ? 'none' : 'flex' }}
                                        className="w-12 h-12 bg-gray-100 rounded border border-gray-300 flex items-center justify-center cursor-pointer"
                                        onClick={() => handleVerGaleriaArticulo(articulo)}
                                        >
                                            <Package size={16} className="text-gray-400" />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-lg text-blue-600">{articulo.nombre}</h3>
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                                articulo.tipo === 'articulo' ? 'bg-blue-100 text-blue-800' 
                                                : articulo.tipo === 'servicio' ? 'bg-purple-100 text-purple-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {articulo.tipo === 'articulo' ? 'Artículo' : articulo.tipo === 'servicio' ? 'Servicio' : 'Cotizar'}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                articulo.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {articulo.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {articulo.es_destacado === 1 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800"><Star size={10} /> Destacado</span>}
                                            {articulo.es_nuevo === 1 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800"><Sparkles size={10} /> Nuevo</span>}
                                            {articulo.en_oferta === 1 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800"><Percent size={10} /> Oferta</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2">
                                    <button onClick={() => toggleCardExpansion(articulo.id)} className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors" title={expandedCards[articulo.id] ? "Contraer detalles" : "Expandir detalles"}>
                                        {expandedCards[articulo.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </button>
                                    <button onClick={() => handleVerArticulo(articulo)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-colors" title="Ver detalles"><Eye size={20} /></button>
                                    <button onClick={() => handleEditarArticulo(articulo)} className="text-green-600 hover:text-green-800 hover:bg-green-100 p-2 rounded-lg transition-colors" title="Editar"><Edit size={20} /></button>
                                    <button onClick={() => handleEliminarArticulo(articulo.id)} className="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-colors" title="Eliminar"><Trash2 size={20} /></button>
                                </div>
                            </div>

                            {/* Contenido principal colapsado/expandido */}
                            <div className="p-4">
                                {!expandedCards[articulo.id] ? (
                                // Vista resumida
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Categoría</p>
                                        <p className="text-sm text-gray-800">
                                            {articulo.categoria_nombre ? (
                                            <span className="px-2 py-0.5 rounded font-semibold" style={{ backgroundColor: `${articulo.categoria_color}20`, color: articulo.categoria_color }}>
                                                {articulo.categoria_nombre}
                                            </span>
                                            ) : <span className="text-gray-400">Sin categoría</span>}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Precio Venta</p>
                                        <p className="text-sm font-bold text-blue-600">${Number(articulo.precio_venta || 0).toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Stock</p>
                                        <p className="text-sm text-gray-800">
                                            {articulo.tipo === 'articulo' ? (
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${articulo.stock_actual <= articulo.stock_minimo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                {articulo.stock_actual}
                                            </span>
                                            ) : <span className="text-gray-400">N/A</span>}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Ubicación</p>
                                        <p className="text-sm text-gray-800">{articulo.ubicacion_ruta || <span className="text-gray-400">N/A</span>}</p>
                                    </div>
                                </div>
                                ) : (
                                // Vista expandida detallada
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex items-start gap-3">
                                        <Tag className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Marca</p>
                                            <p className="text-sm text-gray-800">{articulo.marca || <span className="text-gray-400">Sin marca</span>}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-start gap-3">
                                        <Boxes className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Categoría</p>
                                            <p className="text-sm text-gray-800">
                                            {articulo.categoria_nombre ? (
                                                <span className="px-2 py-0.5 rounded font-semibold" style={{ backgroundColor: `${articulo.categoria_color}20`, color: articulo.categoria_color }}>
                                                {articulo.categoria_nombre}
                                                </span>
                                            ) : <span className="text-gray-400">Sin categoría</span>}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Building2 className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Proveedor</p>
                                            <p className="text-sm text-gray-800">{articulo.proveedor_nombre || <span className="text-gray-400">Sin proveedor</span>}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <MapPin className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Ubicación</p>
                                            <p className="text-sm text-gray-800">{articulo.ubicacion_ruta || <span className="text-gray-400">N/A</span>}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <DollarSign className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Precios</p>
                                            <p className="text-sm font-bold text-blue-600">${Number(articulo.precio_venta || 0).toFixed(2)}</p>
                                            {articulo.precio_mayorista > 0 && <p className="text-xs text-green-600">Mayor: ${Number(articulo.precio_mayorista).toFixed(2)}</p>}
                                            {articulo.tasa_impuesto > 0 && <p className="text-xs text-gray-500">IVA: {articulo.tasa_impuesto}%</p>}
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Package className="text-gray-400 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-xs text-gray-500 font-medium">Stock</p>
                                            {articulo.tipo === 'articulo' ? (
                                            <>
                                                <p className="text-sm text-gray-800">
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${articulo.stock_actual <= articulo.stock_minimo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                    {articulo.stock_actual} actual
                                                </span>
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">Mínimo: {articulo.stock_minimo}</p>
                                            </>
                                            ) : <p className="text-sm text-gray-400">N/A</p>}
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Paginación */}
            {articulosFiltrados.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                        Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, articulosFiltrados.length)} de {articulosFiltrados.length} registros
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                        <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                        Primero
                        </button>
                        <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                        Anterior
                        </button>
                        <span className="px-3 py-1 text-sm font-medium">
                        Página {currentPage} de {totalPages}
                        </span>
                        <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                        Siguiente
                        </button>
                        <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                        Último
                        </button>
                    </div>
                </div>
            )}
        </div>

        {/* Modales */}
        {showModal && (
            <ArticuloModal
            mode={modalMode}
            articulo={articuloSeleccionado}
            onClose={() => setShowModal(false)}
            onSuccess={handleModalSuccess}
            showFeedback={showFeedback}
            />
        )}

        {showGaleriaModal && (
            <GaleriaModal
            articulo={articuloGaleria}
            imagenes={imagenesGaleria}
            onClose={() => {
                setShowGaleriaModal(false);
                setArticuloGaleria(null);
                setImagenesGaleria([]);
            }}
            />
        )}

        {/* Feedback Modal */}
        <FeedbackModal
            {...feedback}
            onClose={closeFeedback}
        />
        </div>
    );
    };

export default ArticulosView;
